/**
 * G-ST-P/OE, ADR-0022: feste, abschliessende Stammdatentabellen der konfigurierten
 * Stuetzringdichtung -- Geometrieklassen und zulaessige Profil/Werkstoff-
 * Kombinationen.
 *
 * Einzige Quelle dieser Tabellen. Seed (`apps/api/prisma/oe-core-data.ts`),
 * Preislogik (`oe-geometry.ts`) und Admin-Pflege (`oe-pricing.service.ts`)
 * referenzieren dieselben Konstanten statt eigener Kopien -- eine Aenderung der
 * Geometrie oder der zulaessigen Profile darf nicht an mehreren Stellen
 * nachgezogen werden muessen.
 *
 * Die String-Literale entsprechen wortgleich den Prisma-Enum-Werten
 * `OeGeometryClass`/`OeRingMaterial`/`OeQuantityTier` -- dieses Paket bleibt
 * bewusst frameworkfrei und kennt `@prisma/client` nicht (ARCHITECTURE.md
 * Abschnitt 3), die Werte muessen deshalb hier als eigener Typ gefuehrt werden.
 */

export type OeGeometryClassKey =
  | 'CLASS_200_400'
  | 'CLASS_400_600'
  | 'CLASS_600_800'
  | 'CLASS_800_1000'
  | 'CLASS_1000_1300';

export type OeRingMaterialKey = 'STEEL_1_4301' | 'STEEL_1_4571';

export type OeQuantityTierKey = 'TIER_1_4' | 'TIER_5_PLUS';

/** Reale K+Z-Werkstoffcodes der drei zulaessigen OE-Profilelastomere. */
export type OeElastomerCode = '04' | '13' | '08';

export interface OeGeometryClassDefinition {
  readonly key: OeGeometryClassKey;
  /** Untere Grenze des Ring-Innendurchmessers. Inklusive nur bei der ersten
   *  Klasse (exakt 200 mm gehoert zur ersten Klasse); sonst exklusiv. */
  readonly ringInnerDiameterLowerBoundMm: number;
  readonly lowerBoundInclusive: boolean;
  /** Obere Grenze des Ring-Innendurchmessers, immer inklusive. */
  readonly ringInnerDiameterUpperBoundMm: number;
  readonly maxRingWidthMm: number;
  readonly maxRingOuterDiameterMm: number;
  readonly allowedSheetThicknessesMm: readonly number[];
}

/** Fuenf Ringgroessenklassen. Unter 200 mm Ring-Innendurchmesser gibt es kein
 *  Angebot; ueber 1.300 mm ebenfalls nicht. */
export const OE_GEOMETRY_CLASSES: readonly OeGeometryClassDefinition[] = [
  {
    key: 'CLASS_200_400',
    ringInnerDiameterLowerBoundMm: 200,
    lowerBoundInclusive: true,
    ringInnerDiameterUpperBoundMm: 400,
    maxRingWidthMm: 60,
    maxRingOuterDiameterMm: 520,
    allowedSheetThicknessesMm: [1.5, 2.0],
  },
  {
    key: 'CLASS_400_600',
    ringInnerDiameterLowerBoundMm: 400,
    lowerBoundInclusive: false,
    ringInnerDiameterUpperBoundMm: 600,
    maxRingWidthMm: 80,
    maxRingOuterDiameterMm: 760,
    allowedSheetThicknessesMm: [1.5, 2.0],
  },
  {
    key: 'CLASS_600_800',
    ringInnerDiameterLowerBoundMm: 600,
    lowerBoundInclusive: false,
    ringInnerDiameterUpperBoundMm: 800,
    maxRingWidthMm: 80,
    maxRingOuterDiameterMm: 960,
    allowedSheetThicknessesMm: [2.0, 2.5],
  },
  {
    key: 'CLASS_800_1000',
    ringInnerDiameterLowerBoundMm: 800,
    lowerBoundInclusive: false,
    ringInnerDiameterUpperBoundMm: 1000,
    maxRingWidthMm: 80,
    maxRingOuterDiameterMm: 1160,
    allowedSheetThicknessesMm: [2.0, 2.5],
  },
  {
    key: 'CLASS_1000_1300',
    ringInnerDiameterLowerBoundMm: 1000,
    lowerBoundInclusive: false,
    ringInnerDiameterUpperBoundMm: 1300,
    maxRingWidthMm: 80,
    maxRingOuterDiameterMm: 1460,
    allowedSheetThicknessesMm: [2.0, 2.5],
  },
];

export const OE_RING_MATERIALS: readonly OeRingMaterialKey[] = [
  'STEEL_1_4301',
  'STEEL_1_4571',
];

export const OE_QUANTITY_TIERS: readonly OeQuantityTierKey[] = [
  'TIER_1_4',
  'TIER_5_PLUS',
];

/** Anzeigetext des Ringwerkstoffs, zugleich die auf Beleg/PDF gedruckte
 *  Positionskennung des Ringes. */
export const OE_RING_MATERIAL_LABEL: Record<OeRingMaterialKey, string> = {
  STEEL_1_4301: '1.4301',
  STEEL_1_4571: '1.4571',
};

export interface OeProfileDefinition {
  /** Administrativ gepflegte Positionskennung: `{Profilbreite}{Werkstoffcode}-OE`.
   *  Kein K+Z-Katalogartikel, durchlaeuft nie Preislistenimport oder
   *  Artikelnummernvalidierung. */
  readonly profileArticleCode: string;
  readonly elastomerCode: OeElastomerCode;
  readonly profileWidthMm: number;
  /** Aufschlag auf den Ziel-Innendurchmesser: Ring-ID = Ziel-ID + Aufschlag. */
  readonly ringIdOffsetMm: number;
  /** Die von diesem Profil verlangte Blechdicke des Ringes -- fest, keine Wahl. */
  readonly requiredSheetThicknessMm: number;
  /** Nominale Bohrungsangabe der Profilbezeichnung, z. B. `6/8` in "18x6/8".
   *  Rein darstellend, ohne Einfluss auf Geometrie oder Preis. */
  readonly nominalBoreLabel: string;
  /** Mindestinnendurchmesser der fertigen Dichtung (nicht Ring-ID, Profil-AD
   *  oder Berechnungsdurchmesser) fuer dieses Profil. Feste technische
   *  Profilkenngroesse, kein administrativ pflegbarer Preiswert -- siehe
   *  `computeOeGeometry`. Der Grenzwert selbst ist zulaessig. */
  readonly minInnerDiameterMm: number;
}

/**
 * Die zwoelf zulaessigen Profil/Werkstoff-Kombinationen. Das 70-mm-Profil ist
 * vollstaendig ausgeschlossen -- keine Vorbereitung in Datenmodell, Seeds, UI,
 * Tests oder Dokumentation.
 */
export const OE_PROFILE_DEFINITIONS: readonly OeProfileDefinition[] = [
  // 15x6/8, nur NBR. Mindest-ID 200 mm.
  {
    profileArticleCode: '1513-OE',
    elastomerCode: '13',
    profileWidthMm: 15,
    ringIdOffsetMm: 15,
    requiredSheetThicknessMm: 1.5,
    nominalBoreLabel: '6/8',
    minInnerDiameterMm: 200,
  },
  // 18x6/8, alle drei Elastomere. Mindest-ID 200 mm.
  {
    profileArticleCode: '1804-OE',
    elastomerCode: '04',
    profileWidthMm: 18,
    ringIdOffsetMm: 16,
    requiredSheetThicknessMm: 1.5,
    nominalBoreLabel: '6/8',
    minInnerDiameterMm: 200,
  },
  {
    profileArticleCode: '1813-OE',
    elastomerCode: '13',
    profileWidthMm: 18,
    ringIdOffsetMm: 16,
    requiredSheetThicknessMm: 1.5,
    nominalBoreLabel: '6/8',
    minInnerDiameterMm: 200,
  },
  {
    profileArticleCode: '1808-OE',
    elastomerCode: '08',
    profileWidthMm: 18,
    ringIdOffsetMm: 16,
    requiredSheetThicknessMm: 1.5,
    nominalBoreLabel: '6/8',
    minInnerDiameterMm: 200,
  },
  // 25x8/10, alle drei Elastomere. Mindest-ID 350 mm.
  {
    profileArticleCode: '2504-OE',
    elastomerCode: '04',
    profileWidthMm: 25,
    ringIdOffsetMm: 23,
    requiredSheetThicknessMm: 2.0,
    nominalBoreLabel: '8/10',
    minInnerDiameterMm: 350,
  },
  {
    profileArticleCode: '2513-OE',
    elastomerCode: '13',
    profileWidthMm: 25,
    ringIdOffsetMm: 23,
    requiredSheetThicknessMm: 2.0,
    nominalBoreLabel: '8/10',
    minInnerDiameterMm: 350,
  },
  {
    profileArticleCode: '2508-OE',
    elastomerCode: '08',
    profileWidthMm: 25,
    ringIdOffsetMm: 23,
    requiredSheetThicknessMm: 2.0,
    nominalBoreLabel: '8/10',
    minInnerDiameterMm: 350,
  },
  // 35x10/12, alle drei Elastomere. Mindest-ID 500 mm.
  {
    profileArticleCode: '3504-OE',
    elastomerCode: '04',
    profileWidthMm: 35,
    ringIdOffsetMm: 23,
    requiredSheetThicknessMm: 2.0,
    nominalBoreLabel: '10/12',
    minInnerDiameterMm: 500,
  },
  {
    profileArticleCode: '3513-OE',
    elastomerCode: '13',
    profileWidthMm: 35,
    ringIdOffsetMm: 23,
    requiredSheetThicknessMm: 2.0,
    nominalBoreLabel: '10/12',
    minInnerDiameterMm: 500,
  },
  {
    profileArticleCode: '3508-OE',
    elastomerCode: '08',
    profileWidthMm: 35,
    ringIdOffsetMm: 23,
    requiredSheetThicknessMm: 2.0,
    nominalBoreLabel: '10/12',
    minInnerDiameterMm: 500,
  },
  // 45x10/12, nur EPDM und NBR -- FKM nicht lieferbar. Mindest-ID 800 mm.
  {
    profileArticleCode: '4504-OE',
    elastomerCode: '04',
    profileWidthMm: 45,
    ringIdOffsetMm: 23,
    requiredSheetThicknessMm: 2.5,
    nominalBoreLabel: '10/12',
    minInnerDiameterMm: 800,
  },
  {
    profileArticleCode: '4513-OE',
    elastomerCode: '13',
    profileWidthMm: 45,
    ringIdOffsetMm: 23,
    requiredSheetThicknessMm: 2.5,
    nominalBoreLabel: '10/12',
    minInnerDiameterMm: 800,
  },
];

/** Sicherheitszuschlag je Dichtung, fest (kein administrativ pflegbarer Wert). */
export const OE_PROFILE_SAFETY_MARGIN_M = 0.2;

/** Alle gueltigen (Geometrieklasse, Blechdicke)-Paare der Ringpreistabelle, in der
 *  Reihenfolge, in der sie im Admin-Raster erwartet werden. 10 Paare. */
export function oeRingGeometryThicknessPairs(): ReadonlyArray<{
  readonly geometryClass: OeGeometryClassKey;
  readonly sheetThicknessMm: number;
}> {
  return OE_GEOMETRY_CLASSES.flatMap((klasse) =>
    klasse.allowedSheetThicknessesMm.map((sheetThicknessMm) => ({
      geometryClass: klasse.key,
      sheetThicknessMm,
    })),
  );
}

/** Alle 40 gueltigen Ringpreis-Kombinationen (Werkstoff x Klasse/Dicke x
 *  Mengenstaffel), in fester Reihenfolge fuer Seed und Admin-Raster. */
export function oeRingPriceCombinations(): ReadonlyArray<{
  readonly ringMaterial: OeRingMaterialKey;
  readonly geometryClass: OeGeometryClassKey;
  readonly sheetThicknessMm: number;
  readonly quantityTier: OeQuantityTierKey;
}> {
  const paare = oeRingGeometryThicknessPairs();
  return OE_RING_MATERIALS.flatMap((ringMaterial) =>
    paare.flatMap((paar) =>
      OE_QUANTITY_TIERS.map((quantityTier) => ({
        ringMaterial,
        geometryClass: paar.geometryClass,
        sheetThicknessMm: paar.sheetThicknessMm,
        quantityTier,
      })),
    ),
  );
}
